import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../hooks/redux';
import { useRBAC } from '../hooks/useRBAC';
import LoginPage from '../pages/auth/LoginPage';
import { loginSuccess, loginStart } from '../store/authSlice';
import { authService, type OtpVerificationResponse } from '../services/authService';
import type { User } from '../types';
import { apiService, type ApiResponse } from '../services';

const LoginContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { setUserProfile, setAppraisalCycle } = useRBAC();
  
  // State for tracking OTP attempts
  const [otpAttempts, setOtpAttempts] = useState<number>(0);
  const [lastAttemptEmail, setLastAttemptEmail] = useState<string>('');

  // Reset OTP attempts when a new email is used
  const resetOtpAttempts = useCallback(() => {
    setOtpAttempts(0);
    setLastAttemptEmail('');
  }, []);

  const handleSendOtp = async (email: string): Promise<ApiResponse<Record<string, unknown>>> => {
    try {
      // Reset OTP attempts when sending new OTP
      if (lastAttemptEmail !== email) {
        resetOtpAttempts();
        setLastAttemptEmail(email);
      }
      
      const response = await authService.sendOtp(email);
      
      // If OTP sent successfully, reset attempts counter
      if (response.status) {
        setOtpAttempts(0);
      }
      
      // Return the response as-is since it's already in ApiResponse format
      return response;
    } catch (error) {
      console.error('Send OTP error:', error);
      
      // Check if it's an API error with response data (this is the 400 Bad Request case)
      if (error && typeof error === 'object' && 'message' in error && 'status_code' in error) {
        // Return the error as an ApiResponse format
        const apiError = error as { message: string; status_code: number; data?: Record<string, unknown> };
        
        // Handle specific HTTP error codes with clear messages
        if (apiError.status_code === 403) {
          return {
            status: false,
            status_code: 403,
            message: apiError.message || 'Access denied. Please contact your administrator.',
            data: {}
          };
        }
        if (apiError.status_code === 401) {
          return {
            status: false,
            status_code: 401,
            message: apiError.message || 'Unauthorized access. Please check your email address.',
            data: {}
          };
        }
        if (apiError.status_code === 502) {
          return {
            status: false,
            status_code: 502,
            message: 'Server unavailable. Try again.',
            data: {}
          };
        }
        if (apiError.status_code === 504) {
          return {
            status: false,
            status_code: 504,
            message: 'Request timed out. Try again.',
            data: {}
          };
        }
        
        return {
          status: false,
          status_code: apiError.status_code || 400,
          message: apiError.message || 'Failed to send OTP',
          data: apiError.data || {}
        };
      }
      
      // Check if it's a network connection error
      if (error instanceof Error) {
        if(error.message.includes('bad gateway')) {
          return {
            status: false,
            status_code: 502,
            message: 'Server unavailable. Try again.',
            data: {}
          };
        }
        if(error.message.includes('gateway timeout') || error.message.includes('504')) {
          return {
            status: false,
            status_code: 504,
            message: 'Request timed out. Try again.',
            data: {}
          };
        }
        if (error.message.includes('ERR_CONNECTION_REFUSED') || error.message.includes('Network Error')) {
          return {
            status: false,
            status_code: 503,
            message: 'Connection failed. Check network.',
            data: {}
          };
        }
        if (error.message.includes('timeout')) {
          return {
            status: false,
            status_code: 408,
            message: 'Request timeout. Try again.',
            data: {}
          };
        }
      }
      
      return {
        status: false,
        status_code: 500,
        message: 'Network error. Try again.',
        data: {}
      };
    }
  };

    const handleVerifyOtp = async (email: string, otp: string): Promise<ApiResponse<OtpVerificationResponse>> => {
      try {
        const response = await authService.verifyOtp(email, otp);
        
        // Check for successful verification using the authService response structure
        if (response.status === true && response.status_code === 200 && response.data) {
          // Set loading state only on success before starting verification
          dispatch(loginStart());
          
          // Reset OTP attempts on successful verification
          setOtpAttempts(0);
          
          // The authService already handles token storage and user data creation
          // Let's get the stored user data from authService

          if(response.data.token !== null) {
             apiService.setAuthToken(response.data.token);
          }
           const storedUser = await authService.getCurrentUser(email);
          
          let user: User;
          if (storedUser.status) {
            user = storedUser.data!;
          } else {
            // Create user object from response data if stored user is not available
            user = {
              id: 'temp-id',
              name: response.data.name || 'User',
              email: response.data.userName || email,
              designation: 'Employee',
              department: 'General'
            };
          }
          
          // Update Redux state - this will set isLoading to false
          dispatch(loginSuccess(user));
          
          // Fetch RBAC profile with permissions after successful login
          try {
            console.log('🔐 Fetching RBAC profile after login...');
            const profileResponse = await authService.getUserProfileWithPermissions(email);
            
            if (profileResponse.status && profileResponse.data) {
              console.log('✅ RBAC profile loaded after login:', profileResponse.data);
              setUserProfile(profileResponse.data);
              
              // Fetch appraisal cycle data
              console.log('📅 Fetching appraisal cycle data after login...');
              try {
                const cycleResponse = await authService.getAppraisalCycle(email);
                
                if (cycleResponse.status && cycleResponse.data) {
                  console.log('✅ Appraisal cycle loaded after login:', cycleResponse.data);
                  setAppraisalCycle(cycleResponse.data);
                } else {
                  console.warn('⚠️ Failed to load appraisal cycle after login:', cycleResponse.message);
                }
              } catch (cycleError) {
                console.error('❌ Error fetching appraisal cycle after login:', cycleError);
              }
            } else {
              console.warn('⚠️ Failed to load RBAC profile after login:', profileResponse.message);
              // Try to use cached profile if available
              const storedProfile = localStorage.getItem('userProfile');
              if (storedProfile) {
                try {
                  const profile = JSON.parse(storedProfile);
                  console.log('📦 Using cached RBAC profile as fallback:', profile);
                  setUserProfile(profile);
                } catch (parseError) {
                  console.error('❌ Failed to parse cached profile:', parseError);
                }
              }
            }
          } catch (profileError) {
            console.error('❌ Error fetching RBAC profile after login:', profileError);
            // Try to use cached profile if available
            const storedProfile = localStorage.getItem('userProfile');
            if (storedProfile) {
              try {
                const profile = JSON.parse(storedProfile);
                console.log('📦 Using cached RBAC profile after error:', profile);
                setUserProfile(profile);
              } catch (parseError) {
                console.error('❌ Failed to parse cached profile:', parseError);
              }
            }
          }
          
          // Clear login persistence data
          localStorage.removeItem('login_step');
          localStorage.removeItem('login_email');
          
          // Wait a bit longer to ensure Redux state and RBAC data are properly updated
          // This prevents the brief flash of error/loading states
          setTimeout(() => {
            navigate(`/profile/${user?.employee_id}`, { replace: true });
          }, 200);
          
          return response;
          
        } else {
          // Handle failed verification - increment attempt count
          const newAttempts = otpAttempts + 1;
          setOtpAttempts(newAttempts);
          
          // Return original API response message
          return response;
        }
      } catch (error) {
        console.error('Verify OTP error:', error);
        
        // Increment attempt count for any OTP verification error
        const newAttempts = otpAttempts + 1;
        setOtpAttempts(newAttempts);
        
        // Check if it's an API error with response data (this is the 400 Bad Request case)
        if (error && typeof error === 'object' && 'message' in error && 'status_code' in error) {
          // Return the error as an ApiResponse format with original message
          const apiError = error as { message: string; status_code: number; data?: OtpVerificationResponse };
          
          // Handle specific access denied errors
          if (apiError.status_code === 403) {
            return {
              status: false,
              status_code: 403,
              message: apiError.message || 'Access denied. You do not have permission to access this system.',
              data: apiError.data || {} as OtpVerificationResponse
            };
          }
          
          if (apiError.status_code === 401) {
            return {
              status: false,
              status_code: 401,
              message: apiError.message || 'Invalid credentials. Please try again.',
              data: apiError.data || {} as OtpVerificationResponse
            };
          }
          
          return {
            status: false,
            status_code: apiError.status_code || 400,
            message: apiError.message,
            data: apiError.data || {} as OtpVerificationResponse
          };
        }
        
        // Check if it's a network connection error
        if (error instanceof Error) {
          if (error.message.includes('ERR_CONNECTION_REFUSED') || error.message.includes('Network Error')) {
            return {
              status: false,
              status_code: 503,
              message: 'Connection failed. Check network.',
              data: {} as OtpVerificationResponse
            };
          }
          if (error.message.includes('timeout')) {
            return {
              status: false,
              status_code: 408,
              message: 'Request timeout. Try again.',
              data: {} as OtpVerificationResponse
            };
          }
          if(error.message.includes('bad gateway')) {
            return {
              status: false,
              status_code: 502,
              message: 'Server unavailable. Try again.',
              data: {} as OtpVerificationResponse
            };
          }
          if(error.message.includes('gateway timeout') || error.message.includes('504')) {
            return {
              status: false,
              status_code: 504,
              message: 'Request timed out. Try again.',
              data: {} as OtpVerificationResponse
            };
          }
        }
        
        return {
          status: false,
          status_code: 500,
          message: 'Network error. Try again.',
          data: {} as OtpVerificationResponse
        };
      }
    };

  const handleResendOtp = async (email: string): Promise<ApiResponse<Record<string, unknown>>> => {
    return await handleSendOtp(email);
  };

  return (
    <LoginPage 
      onSendOtp={handleSendOtp}
      onVerifyOtp={handleVerifyOtp}
      onResendOtp={handleResendOtp}
    />
  );
};

export default LoginContainer;
